import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../utils/api';

const C = {
  navy:     '#0C1A36',
  navy2:    '#112240',
  blue:     '#1A7FBE',
  teal:     '#00A8CC',
  green:    '#3DAA4A',
  gold:     '#F5C800',
  offwhite: '#EDF1FF',
  muted:    '#7A90B8',
  red:      '#C0223A',
};

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

function StatusBadge({ step }) {
  const map = {
    active:          { label: 'Active',    color: C.green },
    suspended:       { label: 'Suspended', color: C.red   },
    pending_payment: { label: 'Pending',   color: C.blue  },
    pending_qb:      { label: 'Setup',     color: C.muted },
  };
  const { label, color } = map[step] || { label: step || 'Unknown', color: C.muted };
  return (
    <span style={{
      background:   color + '22',
      border:       `1px solid ${color}`,
      color,
      borderRadius: '20px',
      padding:      '2px 10px',
      fontSize:     '0.75rem',
      fontWeight:   '600',
      whiteSpace:   'nowrap',
    }}>
      {label}
    </span>
  );
}

function StatCard({ label, value }) {
  return (
    <div style={{
      background:   C.navy2,
      borderTop:    `3px solid ${C.teal}`,
      borderRadius: '8px',
      padding:      '18px 22px',
      flex:         '1 1 0',
      minWidth:     '120px',
    }}>
      <div style={{ color: C.muted, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>{label}</div>
      <div style={{ color: C.offwhite, fontSize: '1.75rem', fontWeight: '700' }}>{value}</div>
    </div>
  );
}

export default function Admin() {
  const navigate = useNavigate();
  const [users,         setUsers]         = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);
  const [actionLoading, setActionLoading] = useState({});

  const myId = (() => {
    try { return JSON.parse(localStorage.getItem('acct4cast_user'))?.id; }
    catch { return null; }
  })();

  async function fetchUsers() {
    setLoading(true);
    setError(null);
    try {
      const res  = await authFetch('/api/admin/users');
      if (res.status === 403) { navigate('/dashboard'); return; }
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to load users'); return; }
      setUsers(data.users);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchUsers(); }, []);

  function setRowLoading(id, val) {
    setActionLoading(prev => ({ ...prev, [id]: val }));
  }

  async function updatePlan(userId, plan) {
    setRowLoading(userId, true);
    try {
      const res = await authFetch(`/api/admin/users/${userId}/plan`, {
        method:  'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ plan }),
      });
      if (!res.ok) {
        const d = await res.json();
        alert(d.error || 'Failed to update plan');
        return;
      }
      await fetchUsers();
    } finally {
      setRowLoading(userId, false);
    }
  }

  async function resetOnboarding(userId, email) {
    setRowLoading(userId, true);
    try {
      const res = await authFetch(`/api/admin/users/${userId}/onboarding`, {
        method:  'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ onboarding_step: 'pending_qb' }),
      });
      if (!res.ok) {
        const d = await res.json();
        alert(d.error || 'Failed to reset onboarding');
        return;
      }
      await fetchUsers();
    } finally {
      setRowLoading(userId, false);
    }
  }

  async function deleteUser(userId, email) {
    if (!window.confirm(`Delete ${email}? This cannot be undone.`)) return;
    setRowLoading(userId, true);
    try {
      const res = await authFetch(`/api/admin/users/${userId}`, { method: 'DELETE' });
      if (!res.ok) {
        const d = await res.json();
        alert(d.error || 'Failed to delete user');
        return;
      }
      await fetchUsers();
    } finally {
      setRowLoading(userId, false);
    }
  }

  const total       = users.length;
  const activeCount = users.filter(u => u.plan === 'active').length;
  const trialCount  = users.filter(u => !u.plan || u.plan === 'trial').length;
  const googleCount = users.filter(u => u.google_id).length;

  const thStyle = {
    color:         C.muted,
    fontSize:      '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    padding:       '10px 14px',
    textAlign:     'left',
    borderBottom:  `1px solid rgba(122,144,184,0.2)`,
    whiteSpace:    'nowrap',
  };

  const tdStyle = {
    padding:      '12px 14px',
    borderBottom: `1px solid rgba(122,144,184,0.1)`,
    verticalAlign:'middle',
  };

  const iconBtn = (color = C.muted) => ({
    background:   'none',
    border:       `1px solid ${color}`,
    borderRadius: '6px',
    color,
    cursor:       'pointer',
    fontSize:     '1rem',
    padding:      '4px 8px',
    lineHeight:   1,
    fontFamily:   'Sora, sans-serif',
  });

  return (
    <div style={{
      background:  C.navy,
      minHeight:   '100vh',
      padding:     '36px 24px 80px',
      fontFamily:  'Sora, sans-serif',
      color:       C.offwhite,
    }}>

      {/* ── Header ── */}
      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ color: C.gold, fontSize: '1.6rem', fontWeight: '700', margin: 0 }}>
          Admin Panel
        </h1>
        <p style={{ color: C.muted, margin: '4px 0 12px', fontSize: '0.9rem' }}>
          User &amp; Account Management
        </p>
        <div style={{ height: '3px', width: '60px', background: C.teal, borderRadius: '2px' }} />
      </div>

      {/* ── Loading / Error ── */}
      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
          <div style={{
            width: '38px', height: '38px',
            border: `4px solid ${C.navy2}`,
            borderTop: `4px solid ${C.teal}`,
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {error && (
        <div style={{
          background:   C.red + '18',
          border:       `1px solid ${C.red}`,
          borderRadius: '8px',
          color:        C.red,
          padding:      '14px 18px',
          marginBottom: '24px',
          fontSize:     '0.9rem',
        }}>
          {error}
        </div>
      )}

      {!loading && !error && (
        <>
          {/* ── Stats Row ── */}
          <div style={{ display: 'flex', gap: '16px', marginBottom: '28px', flexWrap: 'wrap' }}>
            <StatCard label="Total Users"  value={total} />
            <StatCard label="Active"       value={activeCount} />
            <StatCard label="Trial"        value={trialCount} />
            <StatCard label="Google Users" value={googleCount} />
          </div>

          {/* ── User Table ── */}
          <div style={{
            background:   C.navy2,
            borderTop:    `3px solid ${C.teal}`,
            borderRadius: '10px',
            overflowX:    'auto',
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>User</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Plan</th>
                  <th style={thStyle}>QB</th>
                  <th style={thStyle}>Joined</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => {
                  const isMe      = u.id === myId;
                  const rowBusy   = !!actionLoading[u.id];
                  return (
                    <tr key={u.id} style={{ opacity: rowBusy ? 0.6 : 1 }}>

                      {/* User */}
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                          <div>
                            <div style={{ color: C.offwhite, fontWeight: '600', fontSize: '0.9rem' }}>
                              {u.name || u.email}
                            </div>
                            {u.name && (
                              <div style={{ color: C.muted, fontSize: '0.78rem' }}>{u.email}</div>
                            )}
                          </div>
                          {u.google_id && (
                            <span style={{
                              background:   C.teal + '22',
                              border:       `1px solid ${C.teal}`,
                              color:        C.teal,
                              borderRadius: '4px',
                              fontSize:     '0.65rem',
                              fontWeight:   '700',
                              padding:      '1px 5px',
                            }}>G</span>
                          )}
                          {u.role === 'admin' && (
                            <span style={{
                              background:   C.gold + '22',
                              border:       `1px solid ${C.gold}`,
                              color:        C.gold,
                              borderRadius: '4px',
                              fontSize:     '0.65rem',
                              fontWeight:   '700',
                              padding:      '1px 5px',
                            }}>Admin</span>
                          )}
                        </div>
                      </td>

                      {/* Status */}
                      <td style={tdStyle}>
                        <StatusBadge step={u.onboarding_step} />
                      </td>

                      {/* Plan */}
                      <td style={tdStyle}>
                        <select
                          value={u.plan || 'trial'}
                          disabled={rowBusy}
                          onChange={e => updatePlan(u.id, e.target.value)}
                          style={{
                            background:   C.navy,
                            border:       `1px solid rgba(122,144,184,0.3)`,
                            borderRadius: '6px',
                            color:        C.offwhite,
                            fontFamily:   'Sora, sans-serif',
                            fontSize:     '0.82rem',
                            padding:      '4px 8px',
                            cursor:       rowBusy ? 'not-allowed' : 'pointer',
                          }}
                        >
                          <option value="trial">trial</option>
                          <option value="active">active</option>
                          <option value="suspended">suspended</option>
                        </select>
                      </td>

                      {/* QB */}
                      <td style={tdStyle}>
                        <span style={{
                          display:         'inline-block',
                          width:           '10px',
                          height:          '10px',
                          borderRadius:    '50%',
                          background:      u.qb_realm_id ? C.green : C.red,
                          boxShadow:       `0 0 6px ${u.qb_realm_id ? C.green : C.red}88`,
                        }} title={u.qb_realm_id ? 'QB Connected' : 'No QB'} />
                      </td>

                      {/* Joined */}
                      <td style={{ ...tdStyle, color: C.muted, fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                        {formatDate(u.created_at)}
                      </td>

                      {/* Actions */}
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          {!isMe && (
                            <>
                              <button
                                onClick={() => resetOnboarding(u.id, u.email)}
                                disabled={rowBusy}
                                style={iconBtn(C.teal)}
                                title="Reset onboarding to pending_qb"
                              >↺</button>
                              <button
                                onClick={() => deleteUser(u.id, u.email)}
                                disabled={rowBusy}
                                style={iconBtn(C.red)}
                                title="Delete user"
                              >🗑</button>
                            </>
                          )}
                          {isMe && (
                            <span style={{ color: C.muted, fontSize: '0.75rem' }}>you</span>
                          )}
                        </div>
                      </td>

                    </tr>
                  );
                })}
                {users.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ ...tdStyle, textAlign: 'center', color: C.muted, padding: '40px' }}>
                      No users found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
